import { Children, cloneElement, isValidElement, type CSSProperties, type ReactElement, type ReactNode } from "react";
import MuiAutocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

// antd <AutoComplete> -> MUI Autocomplete (freeSolo text-with-suggestions).
// onSearch(text) as the user types; onSelect(value, option) on pick;
// options [{value,label}]. renderInput defaults to a small TextField.

export interface AutoCompleteOption {
  value: string | number;
  label?: ReactNode;
  [k: string]: unknown;
}

/* AutoComplete is used very heterogeneously (search-select, option groups,
   antd DefaultOptionType). Handler/option types are intentionally loose at this
   compat boundary — matching antd's own permissive typing. */
/* eslint-disable @typescript-eslint/no-explicit-any */
export interface AutoCompleteProps {
  options?: any[];
  value?: string;
  defaultValue?: string;
  // Method-signature form so call sites with narrower option types (e.g.
  // (v: string, opt: DefaultOptionType) => void) are accepted (bivariant params).
  onChange?(value: any, option?: any): void;
  onSearch?: (text: string) => void;
  onSelect?(value: any, option?: any): void;
  onClear?: () => void;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  notFoundContent?: ReactNode;
  filterOption?: boolean | ((input: string, option?: any) => boolean);
  style?: CSSProperties;
  className?: string;
  id?: string;
  "data-cy"?: string;
  "data-testid"?: string;
  // accepted for antd compat (no MUI equivalent / handled implicitly):
  dropdownMatchSelectWidth?: number | boolean;
  popupMatchSelectWidth?: number | boolean;
  backfill?: boolean;
  autoFocus?: boolean;
  showSearch?: boolean;
  showArrow?: boolean;
  dropdownStyle?: import("react").CSSProperties;
  searchValue?: string;
  children?: ReactNode;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// Sentinel value for the disabled synthetic "no matches" option (see below).
const NOT_FOUND_VALUE = "__autocomplete_not_found__";

// getOptionLabel drives the input text + freeSolo; must be a string. antd allows
// a JSX `label` (rendered in the dropdown, not searched) — fall back to `value`
// so a JSX label doesn't stringify to "[object Object]".
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const labelOf = (o: any): string => {
  if (typeof o === "string") return o;
  if (typeof o?.label === "string") return o.label;
  return String(o?.value ?? "");
};

// Derive options from antd-style <Option value>label</Option> children when
// the `options` prop isn't used (AutoComplete-as-Select pattern).
const childrenToOptions = (children: ReactNode): AutoCompleteOption[] =>
  Children.toArray(children)
    .filter(isValidElement)
    .map((c) => {
      const el = c as ReactElement<{ value?: string | number; children?: ReactNode }>;
      return { value: el.props.value ?? "", label: el.props.children };
    });

// Render an antd custom-input child (e.g. Input.TextArea) as a MUI TextField that
// MUI can anchor its popup to, while the call site keeps control of value/onChange.
/* eslint-disable @typescript-eslint/no-explicit-any */
const renderCustomInput = (params: any, child: ReactElement) => {
  const cp = child.props as Record<string, any>;
  const auto = cp.autoSize && typeof cp.autoSize === "object" ? cp.autoSize : undefined;
  const multiline = child.type === "textarea" || !!(cp.autoSize || cp.rows || cp.multiline);
  // MUI controls the input value on its own via inputProps.value; drop it so the
  // call site's value wins, but keep MUI's ref/a11y wiring. Keep MUI's onChange:
  // it is the ONLY thing that reopens the popup as you type, and InputBase runs
  // it alongside the call site's onChange (below). Without it the popup opens
  // only on a mousedown in the input, so any close -- Enter-to-send ends the turn
  // with a freeSolo "createOption" that closes it -- left the suggestion menu
  // dead until the user clicked back in.
  const { value: _v, ...muiInputProps } = params.inputProps ?? {};
  return (
    <TextField
      InputProps={params.InputProps}
      inputProps={{
        ...muiInputProps,
        "data-cy": cp["data-cy"],
        "data-testid": cp["data-testid"],
        "aria-label": cp["aria-label"],
      }}
      // MUI's .MuiAutocomplete-inputRoot vertically CENTERS its input (fine for a
      // single-line combobox). For a multiline textarea that centering pushes the
      // first line above the input's top edge as it grows — you see only the
      // bottoms of the top line's letters. Top-align so the first line is never
      // clipped while the composer expands.
      sx={multiline ? { "& .MuiInputBase-root": { alignItems: "flex-start" } } : undefined}
      multiline={multiline}
      minRows={auto?.minRows ?? cp.rows}
      maxRows={auto?.maxRows}
      value={cp.value}
      defaultValue={cp.defaultValue}
      onChange={cp.onChange}
      onKeyDown={cp.onKeyDown}
      onPasteCapture={cp.onPasteCapture}
      placeholder={cp.placeholder}
      id={cp.id}
      disabled={cp.disabled ?? params.disabled}
      size="small"
      fullWidth
    />
  );
};
/* eslint-enable @typescript-eslint/no-explicit-any */

export const AutoComplete = ({
  options,
  value,
  onChange,
  onSearch,
  onSelect,
  onClear,
  placeholder,
  disabled,
  allowClear = true,
  notFoundContent,
  filterOption,
  style,
  className,
  id,
  autoFocus,
  children,
  "data-cy": dataCy,
  "data-testid": dataTestid,
}: AutoCompleteProps) => {
  // antd supports a custom input child: <AutoComplete options><Input.TextArea/></AutoComplete>.
  // When `options` is provided AND children is a single element, treat that
  // element as the input (rendered via renderInput); otherwise children are
  // <Option> entries. With a custom input the child owns value/onChange, so we
  // don't filter (call sites pre-filter `options`).
  const customInput =
    options !== undefined && isValidElement(children) ? (children as ReactElement) : null;
  // Disable MUI's client-side filtering for server-search (filterOption={false}
  // or an onSearch handler) and custom-input cases — the call site owns `options`.
  // Otherwise MUI filters by getOptionLabel and hides pre-filtered/JSX-label options.
  const passthroughFilter = customInput || filterOption === false || !!onSearch;
  const rawOptions = options ?? (children && !customInput ? childrenToOptions(children) : []);
  // antd grouped options are [{ label, options: [...] }]. MUI has no nested-options
  // concept, so flatten to a flat list tagged with a group key + render group headers
  // via groupBy/renderGroup — otherwise the nested options never render.
  const isGrouped =
    !customInput &&
    Array.isArray(rawOptions) &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rawOptions.some((o: any) => o && Array.isArray(o.options));
  const groupLabels: Record<string, ReactNode> = {};
  const baseOptions = isGrouped
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (rawOptions as any[]).flatMap((g: any, i: number) => {
        const key = String(i);
        if (Array.isArray(g.options)) {
          groupLabels[key] = g.label;
          return g.options.map((o: Record<string, unknown>) => ({ ...o, __group: key }));
        }
        return [{ ...g, __group: key }];
      })
    : rawOptions;
  // MUI suppresses noOptionsText whenever freeSolo is on (and freeSolo is needed
  // so typing opens the popup, matching antd). So surface antd's notFoundContent
  // as one disabled synthetic option that renders in the open popup instead.
  const showNotFound =
    !customInput && notFoundContent != null && Array.isArray(baseOptions) && baseOptions.length === 0;
  const effectiveOptions = showNotFound
    ? [{ value: NOT_FOUND_VALUE, label: notFoundContent, disabled: true }]
    : baseOptions;
  return (
    <MuiAutocomplete
      // MUI spreads unknown props onto the root div; forward data-cy/data-testid
      // there so `[data-cy=...] input` selectors (antd parity) resolve.
      data-cy={dataCy}
      data-testid={dataTestid}
      // fill the container (antd default) so inputs like the chat box don't shrink
      // to a sliver in narrow panels.
      fullWidth
      freeSolo
      disableClearable={!allowClear}
      options={effectiveOptions}
      getOptionLabel={labelOf}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getOptionDisabled={(o: any) => o?.value === NOT_FOUND_VALUE || !!o?.disabled}
      {...(isGrouped
        ? {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            groupBy: (o: any) => String(o?.__group ?? ""),
            renderGroup: (params: { key: string | number; group: string; children?: ReactNode }) => (
              <li key={params.key}>
                {groupLabels[params.group] != null ? (
                  <div style={{ padding: "4px 12px", fontWeight: 600 }}>{groupLabels[params.group]}</div>
                ) : null}
                <ul style={{ padding: 0 }}>{params.children}</ul>
              </li>
            ),
          }
        : {})}
      // antd renders each option's `label` (may be JSX) in the dropdown; MUI's
      // default renderOption uses getOptionLabel (string only), so render label here.
      renderOption={
        customInput
          ? undefined
          : (liProps, option) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const o = option as any;
              // MUI forces options to min-height 48px + wide padding, which makes
              // rich options (e.g. the portfolio search: name + owner + date) render
              // oversized. Let them size to content with tighter padding.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const { style: liStyle, ...restLi } = liProps as any;
              return (
                <li
                  {...restLi}
                  style={{ minHeight: 0, paddingTop: 4, paddingBottom: 4, ...(liStyle || {}) }}
                  key={String(o?.__group ?? "") + ":" + String(o?.value ?? labelOf(o))}
                >
                  {o?.label ?? labelOf(o)}
                </li>
              );
            }
      }
      inputValue={value ?? undefined}
      disabled={disabled}
      noOptionsText={notFoundContent ?? "No results"}
      {...(passthroughFilter ? { filterOptions: (x: unknown[]) => x } : {})}
      onInputChange={(_e, val, reason) => {
        if (customInput) return;
        // antd onClear: the clear "x". Because this wrapper is always freeSolo, MUI
        // surfaces the clear via onInputChange(reason="clear") (not onChange), so
        // handle it here.
        if (reason === "clear") return onClear?.();
        // Only react to real user typing — MUI also fires reason="reset" on selection
        // (val = picked label), which would re-search the chosen label and dirty the input.
        if (reason !== "input") return;
        onChange?.(val);
        onSearch?.(val);
      }}
      onChange={(_e, opt) => {
        if (opt && typeof opt === "object" && opt.value !== NOT_FOUND_VALUE) onSelect?.(opt.value, opt);
      }}
      style={style}
      className={className}
      renderInput={(params) =>
        customInput
          ? // Custom-input (e.g. antd Input.TextArea): render a MUI TextField that
            // MUI anchors (InputProps.ref + a11y) but the call site controls
            // (value/onChange/onKeyDown). data-cy/aria-label land on the <textarea>
            // via inputProps so `textarea[data-cy=...]` selectors resolve.
            renderCustomInput(params, customInput)
          : (
            // MUI Autocomplete owns the input's id via params.inputProps; merge our
            // id/data-testid there so `input#id` / `input[data-testid]` resolve (antd parity).
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                ...(id ? { id } : {}),
                ...(dataTestid ? { "data-testid": dataTestid } : {}),
              }}
              placeholder={placeholder}
              size="small"
              autoFocus={autoFocus}
            />
          )
      }
    />
  );
};

// antd/rc-select DefaultOptionType: value/label + arbitrary extra fields (any,
// matching antd) so call sites accessing custom option props (e.g. option.data) work.
/* eslint-disable @typescript-eslint/no-explicit-any */
export interface DefaultOptionType {
  value?: string | number | null;
  label?: ReactNode;
  [k: string]: any;
}
/* eslint-enable @typescript-eslint/no-explicit-any */
export default AutoComplete;
